import { getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import { GroupHeader } from "@components/formGroup/GroupHeader";
import { formatMinutesToHoursString } from "@components/inputs/date/utils";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { ifAll, ifAny, IFieldDefFn, IGetValueArgs, not, TGetValueFn } from "@components/smart/FieldInfo";
import { ActionButtonPosition } from "@components/smart/smartFastEntryList";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IFormGroupDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import { TFormatterFn } from "@components/smart/smartTable/SmartTable.utils";
import { TCellValue } from "@components/table";
import { getRouteByDocumentType } from "@odata/EntityTypes";
import {
    CurrencyEntity,
    DocumentEntity,
    DocumentTypeEntity,
    EntitySetName,
    EntityTypeName,
    IPrPayslipEmploymentEntity,
    IPrPayslipEntity,
    IPrPayslipLiabilityReceivableEntity,
    PrAttendanceEntity,
    PrDeductionEntity,
    PrEmployeeEntity,
    PrEmploymentEntity,
    PrEmploymentTypeEntity,
    PrExtraSalaryComputationTypeEntity,
    PrExtraSalaryTypeEntity,
    PrInsuranceCompanyEntity,
    PrInsuranceCompanyTypeEntity,
    PrPayslipDeductionEntity,
    PrPayslipEmploymentEntity,
    PrPayslipEmploymentExtraSalaryEntity,
    PrPayslipEmploymentSalaryComponentEntity,
    PrPayslipEntity,
    PrPayslipInsuranceBenefitEntity,
    PrPayslipLiabilityReceivableEntity,
    PrSalaryComponentTemporalEntity
} from "@odata/GeneratedEntityTypes";
import {
    DocumentTypeCode,
    PrAttendanceStatusCode,
    PrDeductionCalculationTypeCode,
    PrExtraSalaryComputationTypeCode,
    PrExtraSalaryTypeCode,
    PrSalaryComponentTypeCode
} from "@odata/GeneratedEnums";
import { getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { IFormatOptions } from "@odata/OData.utils";
import { universalPersonNameFormatter } from "@pages/admin/users/Users.utils";
import { userNameWithAvatarTableFormatter } from "@pages/admin/users/UsersDef";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { DialogType, isLegislativeExtraSalary, } from "@pages/payroll/employment/Employment.utils";
import { isPartOfLegislativeExtraSalaryItem } from "@pages/payroll/employmentTemplate/EmploymentTemplateDef";
import { getExtraSalaryValueUnit } from "@pages/payroll/extraSalary/ExtraSalary.utils";
import LegislativeExtraSalariesSmartFastEntryList
    from "@pages/payroll/extraSalary/LegislativeExtraSalariesSmartFastEntryList";
import DeductionsEntryList from "@pages/payroll/payslip/DeductionsEntryList";
import AttendanceOverview from "@pages/payroll/payslip/overview/AttendanceOverview";
import TotalOverview from "@pages/payroll/payslip/overview/TotalOverview";
import {
    getTotalOverviewAdditionalProperties,
    getTotalOverviewEmploymentAdditionalProperties
} from "@pages/payroll/payslip/overview/TotalOverview.utils";
import {
    AttendanceOverviewProps,
    currentEmploymentPath,
    fullDayFormatter,
    isCustomPayslipDeduction,
    isPayslipEmploymentVisible,
    payslipCurrencyScaleFormatter,
    payslipCurrencyUnit,
    renderPayslipEmploymentTabs,
    tabsPath
} from "@pages/payroll/payslip/Payslip.utils";
import PayslipFormView from "@pages/payroll/payslip/PayslipFormView";
import {
    getSalaryComponentFieldDefinition,
    getSalaryComponentTypesForForm,
    getSalaryEnumNamespaceNames
} from "@pages/payroll/salaryComponent/SalaryComponent.utils";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import i18next from "i18next";
import { reduce } from "lodash";
import React from "react";

import { BasicInputSizes, FastEntryInputSizes, FieldType, LabelStatus, Sort, ValidatorType } from "../../../enums";
import { ColoredText } from "../../../global.style";
import { TValue } from "../../../global.types";
import BindingContext, { createPath } from "../../../odata/BindingContext";
import { ROUTE_EMPLOYEES } from "../../../routes";
import CurrencyType from "../../../types/Currency";
import { currencyScaleFormatter } from "../../../types/Number";
import { IFormDef } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { IDefinition, IGetDefinition } from "../../PageUtils";

export const attendanceOverviewPath = BindingContext.localContext("attendanceOverview");
export const deductionsPath = BindingContext.localContext("deductions");
export const calculationsOverviewPath = BindingContext.localContext("calculationsOverview");

export const employmentTypesPath = BindingContext.localContext("employmentTypes");
export const attendancePath = BindingContext.localContext("attendance");

export const LegislativeExtraSalaryLocalPath = BindingContext.localContext("LegislativeExtraSalaries");
export const ExtraSalaryAmountPath = BindingContext.localContext("ExtraSalaryAmount");
export const ToPayOutPath = BindingContext.localContext("ToPayOut");

export const salaryComponentsGroupId = "SalaryComponents";
export const extraSalariesGroupId = "ExtraSalaries";

export const CurrentPeriodWorkDaysPath = BindingContext.localContext("CurrentPeriodWorkDays");
export const CurrentPeriodHolidaysPath = BindingContext.localContext("CurrentPeriodHolidays");

const isNotOneTimeSalaryComponent: TGetValueFn<boolean> = (args) => {
    const oneTimeBc = args.bindingContext.getParent().navigate(PrPayslipEmploymentSalaryComponentEntity.IsOneTime);
    return !args.storage.getValue(oneTimeBc);
};

const isNotOneTimeExtraSalary: TGetValueFn<boolean> = (args) => {
    const oneTimeBc = args.bindingContext.getParent().navigate(PrPayslipEmploymentExtraSalaryEntity.IsOneTime);
    return !args.storage.getValue(oneTimeBc);
};

const isOvertimeLegislativeExtraSalary: TGetValueFn<boolean> = (args) => {
    const parentBc = args.bindingContext.getParent();
    if (!parentBc.getKey()) {
        // label in smart fast entry list -> always visible
        return true;
    }
    const typeCodeBc = parentBc
            .navigate(PrPayslipEmploymentExtraSalaryEntity.Type)
            .navigate(PrExtraSalaryTypeEntity.Code);
    const typeCode = args.storage.getValue(typeCodeBc);
    return typeCode === PrExtraSalaryTypeCode.Overtime;
};

const hasLiabilitiesOrReceivables: TGetValueFn<boolean> = (args) => {
    const payslip = args.storage.data.entity as IPrPayslipEntity;
    return payslip?.LiabilitiesAndReceivables?.length > 0;
};

const hasInsuranceBenefits: TGetValueFn<boolean> = (args) => {
    const payslip = args.storage.data.entity as IPrPayslipEntity;
    return payslip?.InsuranceBenefits?.length > 0;
};

const extraSalaryAmountFormatter: TFormatterFn = (_, { storage, bindingContext }) => {
    let amount: number;
    const itemBc = bindingContext.getParent();
    const typeCode = storage.getValue(itemBc.navigate(PrPayslipEmploymentExtraSalaryEntity.ComputationType))?.Code;
    const value = storage.getValue(itemBc.navigate(PrPayslipEmploymentExtraSalaryEntity.ValueOverride));
    if (typeCode === PrExtraSalaryComputationTypeCode.AverageHourlySalaryPercentage) {
        const averageSalaryAmount = storage.getValue(itemBc.getParent().navigate(PrPayslipEmploymentEntity.AverageSalaryOverride));
        amount = averageSalaryAmount * value / 100;
    } else {
        amount = value as number;
    }
    const length = storage.getValue(itemBc.navigate(PrPayslipEmploymentExtraSalaryEntity.Length)) ?? 0;
    return CurrencyType.format(amount * length, { currency: getCompanyCurrency(storage.context) });
};

export enum PayslipFormAction {
    ConnectLiabilities = "ConnectLiabilities",
    ConnectReceivables = "ConnectReceivables",
    PastCorrections = "PastCorrections"
}

const workedHoursFormatter: TFormatterFn = (_, args: IFormatOptions<IPrPayslipEntity>) => {
    const worked = reduce(args.entity.Employments, (acc, e) => {
        return acc + e.Worked;
    }, 0);
    return formatMinutesToHoursString(worked);
};

const getDeductionTypeCode = (args: IGetValueArgs): PrDeductionCalculationTypeCode => {
    const parentBc = args.bindingContext.getParent();
    return args.storage.getValue(parentBc.navigate(PrPayslipDeductionEntity.CalculationTypeCode)) as PrDeductionCalculationTypeCode;
};

const hasCustomDeduction = (args: IGetValueArgs): boolean => {
    const deductions = args.storage.getValueByPath(PrPayslipEntity.Deductions);
    return deductions?.some(isCustomPayslipDeduction);
};

export const getDefinitions: IGetDefinition = (): IDefinition => {
    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrPayslip}Table`,
        additionalProperties: [{
            id: createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Attendance, PrAttendanceEntity.StatusCode)
        }, {
            id: PrPayslipEntity.OverrideStatusCode
        }],
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            isValueHelp: true,
            defaultFilters: [
                createPath(PrPayslipEntity.Employee, PrEmployeeEntity.NumberOurs),
                createPath(PrPayslipEntity.Employee, PrEmployeeEntity.FirstName),
                createPath(PrPayslipEntity.Employee, PrEmployeeEntity.LastName),
                PrPayslipEntity.AggregatedGrossSalary
            ],
            filterDefinition: {
                [createPath(PrPayslipEntity.Employee, PrEmployeeEntity.NumberOurs)]: {},
                [createPath(PrPayslipEntity.Employee, PrEmployeeEntity.FirstName)]: {},
                [createPath(PrPayslipEntity.Employee, PrEmployeeEntity.LastName)]: {},
                [PrPayslipEntity.AggregatedGrossSalary]: {
                    formatter: (val, args: IFormatOptions<IPrPayslipEntity>) => {
                        return CurrencyType.format(val, { currency: getCompanyCurrency(args.context) });
                    }
                },
            }
        }],
        columns: [
            createPath(PrPayslipEntity.Employee, PrEmployeeEntity.NumberOurs),
            createPath(PrPayslipEntity.Employee, PrEmployeeEntity.LastName),
            employmentTypesPath,
            attendancePath,
            createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Worked),
            PrPayslipEntity.AggregatedGrossSalary
        ],
        columnDefinition: {
            [createPath(PrPayslipEntity.Employee, PrEmployeeEntity.NumberOurs)]: {
                label: i18next.t("Payslip:Table.NumberOurs"),
                formatter: (val: TValue, {
                    entity,
                    storage
                }: IFormatOptions<IPrPayslipEntity>): TCellValue => {
                    const { Id } = entity.Employee;
                    return getTableIntentLink(val as string, {
                        route: `${ROUTE_EMPLOYEES}/${Id}`,
                        context: storage.context,
                        storage: storage
                    });
                }
            },
            [createPath(PrPayslipEntity.Employee, PrEmployeeEntity.LastName)]: {
                label: i18next.t("Payslip:Table.Employee"),
                additionalProperties: [{ id: `/${createPath(PrPayslipEntity.Employee, PrEmployeeEntity.FirstName)}` }],
                formatter: (val, args: IFormatOptions<IPrPayslipEntity>) => {
                    return userNameWithAvatarTableFormatter(val, {
                        ...args,
                        entity: args.entity.Employee
                    });
                }
            },
            [employmentTypesPath]: {
                label: i18next.t("Payslip:Table.EmploymentsType"),
                fieldSettings: {
                    disableSort: true
                },
                additionalProperties: [{
                    id: `${createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Employment, PrEmploymentEntity.Type, PrEmploymentTypeEntity.Name)}`
                }],
                formatter: (_, args: IFormatOptions<IPrPayslipEntity>) => {
                    let name: string;
                    if (args.entity.Employments.length === 1) {
                        name = args.entity.Employments[0]?.Employment?.Type?.Name;
                    } else {
                        name = i18next.t("Payslip:Table.MultipleEmployments");
                    }

                    return {
                        value: <b>{name}</b>,
                        tooltip: name
                    };
                }
            },
            [attendancePath]: {
                label: i18next.t("Payslip:Table.Attendance"),
                fieldSettings: {
                    disableSort: true
                },
                additionalProperties: [{
                    id: `${createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Attendance, PrAttendanceEntity.StatusCode)}`
                }],
                formatter: (_, args: IFormatOptions<IPrPayslipEntity>) => {
                    if (args.entity.Employments?.some(e => e.Attendance.StatusCode === PrAttendanceStatusCode.ToProcess)) {
                        return {
                            value: <ColoredText
                                    color={"C_SEM_text_bad"}>{i18next.t("Payslip:Table.AttendanceNotClosed")}</ColoredText>,
                            tooltip: i18next.t("Payslip:Table.AttendanceNotClosed")
                        };
                    }
                    return {
                        value: <ColoredText
                                color={"C_SEM_text_good"}>{i18next.t("Payslip:Table.AttendanceClosed")}</ColoredText>,
                        tooltip: i18next.t("Payslip:Table.AttendanceClosed")
                    };
                },
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Worked)]: {
                formatter: workedHoursFormatter
            },
            [PrPayslipEntity.AggregatedGrossSalary]: {
                formatter: (val, args: IFormatOptions<IPrPayslipEntity>) => {
                    return CurrencyType.format(val, { currency: getCompanyCurrency(args.context) });
                }
            }
        },
        title: i18next.t("Payslip:Table.Title"),
        initialSortBy: [{ id: createPath(PrPayslipEntity.Employee, PrEmployeeEntity.NumberOurs), sort: Sort.Desc }]
    };

    const employmentsBaseDef: Partial<IFormGroupDef> = {
        collection: PrPayslipEntity.Employments,
        collectionOrder: PrPayslipEmploymentEntity.DateCreated,
        isVisible: isPayslipEmploymentVisible,
    };
    const typeDef = getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.Type);

    const hasNonZeroValue: TGetValueFn<boolean> = (args) =>
            args.storage.getValue(args.bindingContext) > 0;

    const form: IFormDef = {
        id: `${EntityTypeName.PrPayslip}Form`,
        title: i18next.t("Payslip:Form.Title"),
        translationFiles: getDefinitions.translationFiles,
        summary: [
            {
                id: createPath(PrPayslipEntity.Employee, PrEmployeeEntity.LastName),
                additionalProperties: [{
                    id: "/" + createPath(PrPayslipEntity.Employee, PrEmployeeEntity.FirstName)
                }],
                formatter: (_, args: IFormatOptions<IPrPayslipEntity>) => {
                    return universalPersonNameFormatter(args.entity.Employee);
                }
            }, {
                id: CurrentPeriodWorkDaysPath,
                label: i18next.t("Payslip:Attendance.WorkFund"),
                updateFromLiveValue: true,
                formatter: (val: TValue) => fullDayFormatter(val as number)
            }, {
                id: CurrentPeriodHolidaysPath,
                label: i18next.t("Payslip:Attendance.Holidays"),
                updateFromLiveValue: true,
                isVisible: hasNonZeroValue,
                formatter: (val: TValue) => fullDayFormatter(val as number)
            }/*, {
                id: createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Worked),
                label: i18next.t("Payslip:Form.Worked"),
                formatter: workedHoursFormatter
            }*/
        ],
        customHeaderActions: [
            {
                id: PayslipFormAction.ConnectLiabilities,
                label: i18next.t("Payslip:Form.ConnectWithLiabilities"),
                iconName: "ConnectLiability"
            }, {
                id: PayslipFormAction.ConnectReceivables,
                label: i18next.t("Payslip:Form.ConnectWithReceivables"),
                iconName: "ConnectReceivable"
            }, {
                id: PayslipFormAction.PastCorrections,
                label: i18next.t("Payslip:Form.PastCorrections"),
                iconName: "PastCorrections"
            }
        ],
        additionalProperties: [
            { id: PrPayslipEntity.Month },
            { id: PrPayslipEntity.Year },
            {
                id: PrPayslipEntity.Employee,
                additionalProperties: [
                    {
                        id: PrEmployeeEntity.SalaryCurrency,
                        additionalProperties: [
                            { id: CurrencyEntity.Code },
                            { id: CurrencyEntity.MinorUnit },
                            { id: CurrencyEntity.Name },
                        ]
                    }
                ]
            },
            {
                id: PrPayslipEntity.Employments,
                additionalProperties: [
                    { id: PrPayslipEmploymentEntity.IsIncludingOvertime /* for OvertimeExtraSalary setting */ },
                    { id: PrPayslipEmploymentEntity.AverageSalary /* for AverageSalaryOverride default */ },
                    {
                        id: PrPayslipEmploymentEntity.Attendance,
                        additionalProperties: [
                            { id: PrAttendanceEntity.StatusCode }
                        ]
                    },
                    {
                        id: PrPayslipEmploymentEntity.Employment,
                        additionalProperties: [
                            { id: PrEmploymentEntity.NumberOurs },
                            { id: PrEmploymentEntity.TypeCode },
                            { id: createPath(PrEmploymentEntity.Type, PrEmploymentTypeEntity.Code) },
                            { id: createPath(PrEmploymentEntity.Type, PrEmploymentTypeEntity.Name) }
                        ]
                    },
                    ...AttendanceOverviewProps.map(item => ({ id: item.id })),
                    ...getTotalOverviewEmploymentAdditionalProperties()
                ],
            },
                ...getTotalOverviewAdditionalProperties(),
            {
                id: PrPayslipEntity.LiabilitiesAndReceivables,
                additionalProperties: [
                    { id: PrPayslipLiabilityReceivableEntity.TransactionAmount },
                    { id: createPath(PrPayslipLiabilityReceivableEntity.TransactionCurrency, CurrencyEntity.Code) },
                ]
            },
            { id: PrPayslipEntity.MealVoucherCount },
            { id: PrPayslipEntity.MealVoucherCountOverride },
            { id: PrPayslipEntity.Deductions },
            {
                id: PrPayslipEntity.InsuranceBenefits,
                additionalProperties: [
                    { id: PrPayslipInsuranceBenefitEntity.Amount },
                    { id: PrPayslipInsuranceBenefitEntity.SymbolVariable },
                    { id: PrPayslipInsuranceBenefitEntity.SymbolSpecific },
                    { id: PrPayslipInsuranceBenefitEntity.SymbolConstant },
                    { id: PrPayslipInsuranceBenefitEntity.RemittanceInformation },
                ]
            },
        ],
        formControl: PayslipFormView,
        fieldDefinition: {
            [calculationsOverviewPath]: {
                type: FieldType.Custom,
                render: (args) => {
                    return <TotalOverview storage={args.storage as FormStorage<IPrPayslipEntity>}/>;
                }
            },
            [createPath(PrPayslipEntity.Employments, attendanceOverviewPath)]: {
                type: FieldType.Custom,
                render: ({ storage }) => {
                    const bc = storage.data.bindingContext.navigate(PrPayslipEntity.Employments);
                    const currentTabId = storage.getValueByPath(currentEmploymentPath);
                    return <AttendanceOverview storage={storage as FormStorage<IPrPayslipEntity>}
                                               bindingContext={bc.addKey(currentTabId)}/>;
                }
            },
            [tabsPath]: {
                type: FieldType.Custom,
                isVisible: (args: IGetValueArgs) => args.storage.getValueByPath(PrPayslipEntity.Employments)?.length > 1,
                render: (args) => {
                    const employments: IPrPayslipEmploymentEntity[] = args.storage.getValueByPath(PrPayslipEntity.Employments);
                    return renderPayslipEmploymentTabs(employments, args.storage as FormStorage<IPrPayslipEntity>);
                }
            },
            // ---------- LiabilitiesAndReceivables fields ----------
            [createPath(PrPayslipEntity.LiabilitiesAndReceivables, PrPayslipLiabilityReceivableEntity.Amount)]: {
                label: i18next.t("Payslip:Form.LinkClearing"),
            },
            [createPath(PrPayslipEntity.LiabilitiesAndReceivables, PrPayslipLiabilityReceivableEntity.Document, DocumentEntity.Amount)]: {
                label: i18next.t("Payslip:Form.LinkAmount"),
            },
            [createPath(PrPayslipEntity.LiabilitiesAndReceivables, PrPayslipLiabilityReceivableEntity.Document, DocumentEntity.DocumentType)]: {
                label: i18next.t("Payslip:Form.LinkType"),
                isRequired: false,
                fieldSettings: {
                    displayName: DocumentTypeEntity.Name
                }
            },
            [createPath(PrPayslipEntity.LiabilitiesAndReceivables, PrPayslipLiabilityReceivableEntity.Document)]: {
                label: i18next.t("Payslip:Form.LinkDocument"),
                fieldSettings: {
                    displayName: DocumentEntity.NumberOurs
                },
                formatter: (_, args: IFormatOptions<IPrPayslipEntity, IPrPayslipLiabilityReceivableEntity>) => {
                    const { item, storage } = args;
                    const { NumberOurs, DocumentType, Id } = item.Document;
                    return getTableIntentLink(NumberOurs as string, {
                        route: `${getRouteByDocumentType(DocumentType?.Code as DocumentTypeCode)}/${Id}`,
                        context: storage.context,
                        storage: storage
                    });
                },
            },
            // ---------- /LiabilitiesAndReceivables fields ----------
            // ---------- Insurance Benefits fields ----------
            [createPath(PrPayslipEntity.InsuranceBenefits, PrPayslipInsuranceBenefitEntity.InsuranceCompany, PrInsuranceCompanyEntity.PrInsuranceCompanyType, PrInsuranceCompanyTypeEntity.Name)]: {
                label: i18next.t("Payslip:Form.InsBenType"),
                isReadOnly: true
            },
            [createPath(PrPayslipEntity.InsuranceBenefits, PrPayslipInsuranceBenefitEntity.InsuranceCompany, PrInsuranceCompanyEntity.Name)]: {
                label: i18next.t("Payslip:Form.InsBenSubject"),
                isReadOnly: true
            },
            [createPath(PrPayslipEntity.InsuranceBenefits, PrPayslipInsuranceBenefitEntity.AmountOverride)]: {
                width: FastEntryInputSizes.XS,
                isRequired: true,
                fieldSettings: {
                    unit: payslipCurrencyUnit
                },
                formatter: payslipCurrencyScaleFormatter,
            },
            [createPath(PrPayslipEntity.InsuranceBenefits, PrPayslipInsuranceBenefitEntity.SymbolVariableOverride)]: {},
            [createPath(PrPayslipEntity.InsuranceBenefits, PrPayslipInsuranceBenefitEntity.SymbolSpecificOverride)]: {},
            [createPath(PrPayslipEntity.InsuranceBenefits, PrPayslipInsuranceBenefitEntity.SymbolConstantOverride)]: {},
            [createPath(PrPayslipEntity.InsuranceBenefits, PrPayslipInsuranceBenefitEntity.RemittanceInformationOverride)]: {
                width: BasicInputSizes.L, /* todo: BasicInputSizes vs. FastEntryInputSizes */
            },
            // ---------- /Insurance Benefits fields ----------
            // ---------- Deduction fields ----------
            [deductionsPath]: {
                type: FieldType.Custom,
                label: i18next.t("Payslip:Form.Deductions"),
                labelStatus: LabelStatus.Removed,
                isVisible: hasCustomDeduction,
                render: ({ props, storage, events }: IFieldDefFn) => {
                    return (<DeductionsEntryList storage={storage as FormStorage} {...props} {...events} />);
                },
                additionalProperties: [
                    { id: createPath(PrPayslipEntity.Deductions, PrPayslipDeductionEntity.Name) },
                    { id: createPath(PrPayslipEntity.Deductions, PrPayslipDeductionEntity.Amount) },
                    { id: createPath(PrPayslipEntity.Deductions, PrPayslipDeductionEntity.Deduction, PrDeductionEntity.TypeCode) },
                ],
                customizationData: {
                    useForCustomization: false
                }
            },
            [createPath(PrPayslipEntity.Deductions, PrPayslipDeductionEntity.AmountOverride)]: {
                width: BasicInputSizes.S,
                fieldSettings: {
                    formatter: currencyScaleFormatter,
                    unit: (args: IGetValueArgs): string => {
                        if (getDeductionTypeCode(args) !== PrDeductionCalculationTypeCode.Fixed) {
                            return "%";
                        }
                        return CurrencyType.getCurrencyUnit(getCompanyCurrency(args.storage.context));
                    },
                }
            },
            [createPath(PrPayslipEntity.Deductions, PrPayslipDeductionEntity.CalculationTypeCode)]: {
                isReadOnly: true,
                isVisible: (args: IGetValueArgs) => getDeductionTypeCode(args) !== PrDeductionCalculationTypeCode.Fixed,
                fieldSettings: {
                    formatter: (val: TValue) => {
                        return i18next.t(`Payslip:Form.DeductionType_${val}`);
                    }
                }
            },
            // ---------- /Deduction fields ----------
            // ---------- SalaryComponent fields ----------
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.Name)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.Name),
                isReadOnly: isNotOneTimeSalaryComponent
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.Type)]: {
                ...typeDef,
                isReadOnly: isNotOneTimeSalaryComponent,
                fieldSettings: {
                    ...typeDef.fieldSettings,
                    transformFetchedItems: (items: ISelectItem[]) => {
                        return items.filter(item => getSalaryComponentTypesForForm().includes(item.id as PrSalaryComponentTypeCode));
                    },
                }
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.RegularSalaryType)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.RegularSalaryType),
                isReadOnly: isNotOneTimeSalaryComponent
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.FixedBonusTaxImpact)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.FixedBonusTaxImpact),
                isReadOnly: isNotOneTimeSalaryComponent
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.FixedBonusPayOutImpact)]: {
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.FixedBonusPayOutImpact),
                isReadOnly: isNotOneTimeSalaryComponent
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.Percentage)]: {
                ...getSalaryComponentFieldDefinition(PrSalaryComponentTemporalEntity.DefaultPercentage)
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.AmountOverride)]: {
                label: i18next.t("Payslip:Form.AmountOverride"),
                ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.Amount)
            },
            // ---------- /SalaryComponent fields ----------
            // ---------- ExtraSalary fields ----------
            [createPath(PrPayslipEntity.Employments, LegislativeExtraSalaryLocalPath)]: {
                type: FieldType.Custom,
                label: i18next.t("Payslip:Form.LegislativeExtraSalaries"),
                labelStatus: LabelStatus.Removed,
                render: ({ props, storage }: IFieldDefFn) => {
                    const bc = props.parentProps.bindingContext
                            .getParent()
                            .navigate(PrEmploymentEntity.ExtraSalaries);
                    const { definition } = (storage as FormStorage).data;
                    const groupDef = definition.groups.find(grp => grp.id === extraSalariesGroupId);
                    const columns = groupDef.lineItems.columns.filter(col => col.id !== PrPayslipEmploymentExtraSalaryEntity.ComputationType);
                    return (<>
                        <GroupHeader title={i18next.t("Payslip:Form.LegislativeExtraSalaries")}></GroupHeader>
                        <LegislativeExtraSalariesSmartFastEntryList {...props}
                                                                    groupDefinitionId={extraSalariesGroupId}
                                                                    extraSalariesBindingContext={bc}
                                                                    columns={columns}
                                                                    isCollapsible={false} />
                        <GroupHeader title={i18next.t("Payslip:Form.OwnExtraSalaries")}></GroupHeader>
                    </>);
                },
                customizationData: {
                    useForCustomization: false
                }
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.ExtraSalaries, PrPayslipEmploymentExtraSalaryEntity.Name)]: {
                width: BasicInputSizes.L,
                isReadOnly: ifAny(isNotOneTimeExtraSalary, isPartOfLegislativeExtraSalaryItem),
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.ExtraSalaries, PrPayslipEmploymentExtraSalaryEntity.ComputationType)]: {
                type: FieldType.ComboBox,
                width: BasicInputSizes.XL,
                isReadOnly: isNotOneTimeExtraSalary,
                defaultValue: PrExtraSalaryComputationTypeCode.AverageHourlySalaryPercentage,
                fieldSettings: {
                    displayName: PrExtraSalaryComputationTypeEntity.Name
                },
                isVisible: not(isPartOfLegislativeExtraSalaryItem)
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.ExtraSalaries, PrPayslipEmploymentExtraSalaryEntity.Length)]: {
                type: FieldType.NumberInput,
                width: BasicInputSizes.XS,
                isRequired: false,
                isReadOnly: ifAll(isNotOneTimeExtraSalary, not(isPartOfLegislativeExtraSalaryItem)),
                fieldSettings: {
                    unit: "h",
                    showSteppers: false,
                    min: 0
                },
                validator: {
                    type: ValidatorType.Number,
                    settings: {
                        min: 0
                    }
                },
                affectedFields: [{ id: ExtraSalaryAmountPath, navigateFromParent: true }]
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.ExtraSalaries, PrPayslipEmploymentExtraSalaryEntity.ValueOverride)]: {
                type: FieldType.NumberInput,
                width: BasicInputSizes.S,
                label: i18next.t("Payslip:Form.ValueOverride"),
                isRequired: not(isNotOneTimeExtraSalary),
                isReadOnly: ifAny(isNotOneTimeExtraSalary, isPartOfLegislativeExtraSalaryItem),
                fieldSettings: {
                    unit: getExtraSalaryValueUnit,
                    showSteppers: false,
                    min: 0
                },
                validator: {
                    type: ValidatorType.Number,
                    settings: {
                        min: 0
                    }
                },
                affectedFields: [{ id: ExtraSalaryAmountPath, navigateFromParent: true }]
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.ExtraSalaries, ExtraSalaryAmountPath)]: {
                type: FieldType.NumberInput,
                width: BasicInputSizes.fitContent,
                label: i18next.t("Payslip:Form.Amount"),
                isReadOnly: true, // always read-only, just calculation
                formatter: extraSalaryAmountFormatter
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.ExtraSalaries, ToPayOutPath)]: {
                type: FieldType.Checkbox,
                label: i18next.t("Payslip:Form.ToPayOut"),
                labelStatus: LabelStatus.Hidden,
                defaultValue: true,
                isVisible: isOvertimeLegislativeExtraSalary,
            },
            // ---------- ExtraSalary fields ----------
            // additional (unsorted) fields
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.AverageSalaryOverride)]: {
                type: FieldType.NumberInput,
                label: i18next.t("Payslip:Form.AverageSalaryOverride"),
                width: BasicInputSizes.M,
                isRequired: true,
                fieldSettings: {
                    unit: "Kč"
                }
            }
        },
        groups: [{
            id: "calculations",
            title: i18next.t("Payslip:Form.Calculations"),
            rows: [[{ id: calculationsOverviewPath }]]
        }, {
            id: "Benefits",
            title: i18next.t("Payslip:Form.Benefits"),
            // this is only section title with "subsections". As we don't have subsections, we simulate it with
            // separate sections and just hide this one if none of the subsections is visible
            isVisible: ifAny(hasInsuranceBenefits)
        },  {
            id: "InsuranceBenefits",
            title: i18next.t("Payslip:Form.InsuranceBenefits"),
            showGroupDivider: false,
            isVisible: hasInsuranceBenefits,
            lineItems: {
                collection: PrPayslipEntity.InsuranceBenefits,
                columns: [
                    { id: createPath(PrPayslipInsuranceBenefitEntity.InsuranceCompany, PrInsuranceCompanyEntity.PrInsuranceCompanyType, PrInsuranceCompanyTypeEntity.Name) },
                    { id: createPath(PrPayslipInsuranceBenefitEntity.InsuranceCompany, PrInsuranceCompanyEntity.Name) },
                    { id: PrPayslipInsuranceBenefitEntity.AmountOverride },
                    { id: PrPayslipInsuranceBenefitEntity.SymbolVariableOverride },
                    { id: PrPayslipInsuranceBenefitEntity.SymbolSpecificOverride },
                    { id: PrPayslipInsuranceBenefitEntity.SymbolConstantOverride },
                    { id: PrPayslipInsuranceBenefitEntity.RemittanceInformationOverride },
                ],
                disableVirtualization: true,
                order: PrPayslipLiabilityReceivableEntity.DateCreated,
                canAdd: false,
                canReorder: false,
                isItemCloneable: false,
                isItemRemovable: false
            }
        }, {
            id: "Deductions",
            title: i18next.t("Payslip:Form.Deductions"),
            rows: [[{ id: deductionsPath }]]
        }, {
            id: "LiabilitiesAndReceivables",
            title: i18next.t("Payslip:Form.LiabilitiesAndReceivables"),
            isVisible: hasLiabilitiesOrReceivables,
            lineItems: {
                collection: PrPayslipEntity.LiabilitiesAndReceivables,
                columns: [
                    { id: PrPayslipLiabilityReceivableEntity.Amount },
                    { id: createPath(PrPayslipLiabilityReceivableEntity.Document, DocumentEntity.Amount) },
                    { id: createPath(PrPayslipLiabilityReceivableEntity.Document, DocumentEntity.DocumentType) },
                    { id: createPath(PrPayslipLiabilityReceivableEntity.Document) }
                ],
                isReadOnly: true,
                disableVirtualization: true,
                order: PrPayslipLiabilityReceivableEntity.DateCreated,
                canReorder: false,
                isItemCloneable: false,
                canAdd: false,
                isItemRemovable: true
            }
        }, {
            id: "EmploymentTabs",
            rows: [[{ id: tabsPath }]]
        }, {
            id: "Attendance",
            title: i18next.t("Payslip:Form.Attendance"),
            rows: [[{ id: attendanceOverviewPath }]],
            ...employmentsBaseDef
        }, {
            id: "Averages",
            title: i18next.t("Payslip:Form.Averages"),
            rows: [[{ id: PrPayslipEmploymentEntity.AverageSalaryOverride }]],
            ...employmentsBaseDef
        }, {
            id: salaryComponentsGroupId,
            title: i18next.t("Payslip:Form.SalaryComponents"),
            ...employmentsBaseDef,
            lineItems: {
                collection: PrPayslipEmploymentEntity.SalaryComponents,
                columns: [
                    { id: PrPayslipEmploymentSalaryComponentEntity.Name },
                    { id: PrPayslipEmploymentSalaryComponentEntity.Type },
                    { id: PrPayslipEmploymentSalaryComponentEntity.RegularSalaryType },
                    { id: PrPayslipEmploymentSalaryComponentEntity.FixedBonusTaxImpact },
                    { id: PrPayslipEmploymentSalaryComponentEntity.FixedBonusPayOutImpact },
                    { id: PrPayslipEmploymentSalaryComponentEntity.Percentage },
                    { id: PrPayslipEmploymentSalaryComponentEntity.AmountOverride },
                ],
                additionalFields: [
                    { id: PrPayslipEmploymentSalaryComponentEntity.Amount },
                ],
                useLabelWrapping: false,
                disableVirtualization: true,
                order: PrPayslipEmploymentSalaryComponentEntity.Id,
                canReorder: false,
                isItemCloneable: false,
                isAddButtonTransparent: true,
                customActionButtons: [{
                    id: DialogType.SalaryComponent,
                    title: i18next.t("Payslip:Form.Pick"),
                    position: ActionButtonPosition.Before,
                    isTransparent: false
                }]
            }
        }, {
            id: extraSalariesGroupId,
            title: i18next.t("Payslip:Form.ExtraSalaries"),
            rows: [
                [{ id: LegislativeExtraSalaryLocalPath }],
            ],
            lineItems: {
                collection: PrPayslipEmploymentEntity.ExtraSalaries,
                columns: [
                    { id: PrPayslipEmploymentExtraSalaryEntity.Name },
                    { id: PrPayslipEmploymentExtraSalaryEntity.ComputationType },
                    { id: PrPayslipEmploymentExtraSalaryEntity.Length },
                    { id: PrPayslipEmploymentExtraSalaryEntity.ValueOverride },
                    { id: ExtraSalaryAmountPath },
                    { id: ToPayOutPath },
                ],
                additionalFields: [
                    { id: PrPayslipEmploymentExtraSalaryEntity.Value },
                    { id: PrPayslipEmploymentExtraSalaryEntity.TypeCode },
                    { id: createPath(PrPayslipEmploymentExtraSalaryEntity.Type, PrExtraSalaryTypeEntity.IsLegallyRequired) }
                ],
                useLabelWrapping: false,
                disableVirtualization: true,
                order: PrPayslipEmploymentExtraSalaryEntity.Id,
                canReorder: false,
                isItemVisible: not(isLegislativeExtraSalary),
                isItemCloneable: false,
                isTheOnlyItemRemovable: true,
                isAddButtonTransparent: true,
                customActionButtons: [{
                    id: DialogType.ExtraSalary,
                    title: i18next.t("Payslip:Form.Pick"),
                    position: ActionButtonPosition.Before,
                    isTransparent: false
                }]
            },
            ...employmentsBaseDef
        }, {
            id: "InsuranceDiscount",
            title: i18next.t("Payslip:Form.InsuranceDiscount"),
            ...employmentsBaseDef
        }]
    };

    return {
        entitySet: EntitySetName.PrPayslips,
        table,
        form
    };
};

getDefinitions.translationFiles = [
    "Payslip", "Common", "Document",
    // selection tableViews
    "ExtraSalary", "SalaryComponent", "Attendance",
    ...getSalaryEnumNamespaceNames(),
    getEnumNameSpaceName(EntityTypeName.PrExtraSalaryComputationType)
];
setDefByEntityType(EntityTypeName.PrPayslip, getDefinitions);